import React, { useCallback, useMemo } from 'react';

/**
 * Create/edit-deal dialog, extracted from ChatDetailsSidebar so the sidebar is
 * about the client card again and this form can be rendered and tested alone.
 */

interface DealFormModalProps {
  editingDealId: number | null;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  dealTitle: string;
  setDealTitle: (v: string) => void;
  dealAmount: string;
  setDealAmount: (v: string) => void;
  dealDiscountPercent?: number;
  setDealDiscountPercent?: (v: number) => void;
  dealStage?: string;
  setDealStage?: (v: string) => void;
  dealItems: any[];
  selectedProdId: string;
  setSelectedProdId: (v: string) => void;
  catalog: any[];
  onAddItemToDeal: () => void;
  onRemoveItemFromDeal: (prodId: number) => void;
  onUpdateItemQty: (prodId: number, qty: number) => void;
}

const STAGES = ['Новая заявка', 'Согласован', 'Ожидает оплаты', 'В доставке', 'Выполнен', 'Отменен'];
const money = (value: number) => value.toLocaleString('ru-RU');

export const DealFormModal: React.FC<DealFormModalProps> = ({
  editingDealId,
  onClose,
  onSubmit,
  dealTitle,
  setDealTitle,
  dealAmount,
  setDealAmount,
  dealDiscountPercent,
  setDealDiscountPercent,
  dealStage,
  setDealStage,
  dealItems,
  selectedProdId,
  setSelectedProdId,
  catalog,
  onAddItemToDeal,
  onRemoveItemFromDeal,
  onUpdateItemQty,
}) => {
  const handleTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setDealTitle(e.target.value), [setDealTitle]);
  const handleStage = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => setDealStage?.(e.target.value), [setDealStage]);
  const handleProduct = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => setSelectedProdId(e.target.value), [setSelectedProdId]);
  const handleAmount = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setDealAmount(e.target.value), [setDealAmount]);
  const handleDiscount = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDealDiscountPercent?.(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)));
  }, [setDealDiscountPercent]);
  const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  const totals = useMemo(() => {
    const itemsSum = dealItems.reduce((sum, item) => sum + (item.price || 0) * (item.qty || 1), 0);
    const original = dealItems.length > 0 ? itemsSum : (parseFloat(dealAmount) || 0);
    const percent = dealDiscountPercent || 0;
    const discount = Math.round((original * (percent / 100)) * 100) / 100;
    return { original, percent, discount, net: Math.max(0, original - discount) };
  }, [dealItems, dealAmount, dealDiscountPercent]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="deal-modal" onClick={stopPropagation}>
        <div className="deal-modal-header">
          <h2>{editingDealId ? `Редактирование заявки #${editingDealId}` : 'Создание новой заявки'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <form onSubmit={onSubmit} className="deal-modal-body">
          <div className="form-group">
            <label htmlFor="deal-title">Название заявки *</label>
            <input id="deal-title" type="text" required placeholder="Например: Заявка на закупку товаров" value={dealTitle} onChange={handleTitle} />
          </div>

          <div className="form-group">
            <label htmlFor="deal-stage">Этап воронки</label>
            <select id="deal-stage" value={dealStage || 'Новая заявка'} onChange={handleStage}>
              {STAGES.map(stage => (
                <option key={stage} value={stage}>{stage === 'Выполнен' ? 'Выполнен (Успех)' : stage}</option>
              ))}
            </select>
          </div>

          <div className="modal-items-section">
            <div className="mis-header">
              <label htmlFor="deal-product">Товары / услуги в заявке</label>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select id="deal-product" className="deal-product-select" value={selectedProdId} onChange={handleProduct}>
                  <option value="">Выбрать из Склада...</option>
                  {catalog.map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {Number(p.price || 0).toLocaleString('ru-RU')} ₽ (остаток {p.stock || 0})</option>
                  ))}
                </select>
                <button type="button" onClick={onAddItemToDeal} disabled={!selectedProdId} className="btn-add-item">+ Добавить</button>
              </div>
            </div>

            {dealItems.length === 0 ? (
              <div className="mis-empty">Позиции из каталога пока не добавлены</div>
            ) : (
              dealItems.map((item, idx) => (
                <div key={item.productId ?? idx} className="item-row deal-item-row">
                  <span className="deal-item-name">{item.name}</span>
                  <div className="deal-item-controls">
                    <button type="button" className="deal-qty-btn" onClick={() => onUpdateItemQty(item.productId, item.qty - 1)} aria-label="Уменьшить количество">-</button>
                    <span className="deal-qty-value">{item.qty}</span>
                    <button type="button" className="deal-qty-btn" onClick={() => onUpdateItemQty(item.productId, item.qty + 1)} aria-label="Увеличить количество">+</button>
                    <span className="deal-item-sum">{money((item.price || 0) * (item.qty || 1))} ₽</span>
                    <button type="button" className="deal-item-remove" onClick={() => onRemoveItemFromDeal(item.productId)} aria-label={`Убрать ${item.name}`}>×</button>
                  </div>
                </div>
              ))
            )}

            <div className="mis-total deal-total-block">
              {dealItems.length === 0 && (
                <div className="deal-total-row">
                  <label htmlFor="deal-amount">Сумма заявки вручную (₽):</label>
                  <input id="deal-amount" type="number" min="0" placeholder="0" value={dealAmount} onChange={handleAmount} className="deal-total-input" />
                </div>
              )}

              <div className="deal-total-row">
                <label htmlFor="deal-discount">Скидка (%):</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input id="deal-discount" type="number" min="0" max="100" placeholder="0" value={dealDiscountPercent || 0} onChange={handleDiscount} className="deal-total-input narrow" />
                  <span className="deal-total-label">%</span>
                </div>
              </div>

              <div className="deal-total-final">
                <div>
                  <span className="deal-total-final-label">Итоговая сумма:</span>
                  {totals.percent > 0 && (
                    <span className="deal-total-discount">(скидка {totals.percent}% = -{money(totals.discount)} ₽)</span>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {totals.percent > 0 && <span className="chat-deal-old-amount">{money(totals.original)} ₽</span>}
                  <b className="deal-total-net">{money(totals.net)} ₽</b>
                </div>
              </div>
            </div>
          </div>

          <div className="deal-modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-save">{editingDealId ? 'Сохранить изменения' : 'Создать заявку'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};
